import React, { useRef, useState, useEffect, use } from 'react';
import { useRouter } from 'next/router';
import { Skeleton, Tabs, Pagination } from 'antd';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import EcoLink from 'eco-design/base/eco-link';
import gtm from 'eco-design-reliance/gtm';
import { getIsPadWidth } from '@elem/utils/devices';
import css from './search.module.scss';
import PageLayout from '@/components/PageLayout';
import AIGenerate from '@/components/Search/AIGenerate';
import AIPress from '@/components/Search/AIPress';
import AIProducts from '@/components/Search/AIProducts';
import AISupports from '@/components/Search/AISupports';
import Press from '@/components/Search/Press';
import Products from '@/components/Search/Products';
import Supports from '@/components/Search/Supports';
import SEOlinksA from '@/components/SEOlinksA';
import Image from '@/components/UI/Image';
import EFAIDownloadIcon from '@/components/UI/svg/Header/Icon/EFAIDownloadIcon';
import EFCoinIcon from '@/components/UI/svg/Header/Icon/EFCoinIcon';
import EFHeaderSearch from '@/components/UI/svg/Header/Icon/EFHeaderSearch';
import EFHeaderSearchForSearch from '@/components/UI/svg/Header/Icon/EFHeaderSearchForSearch';
import EFStoreIcon from '@/components/UI/svg/Header/Icon/EFStoreIcon';
import seoconfig from '@/seoconfig/search';
import { getSearchRes, getAISearchRes } from '@/service';
import { useLang } from '@/i18n';
import { isEmpty } from 'lodash';
import { loginUser } from '@/utils/cookie';

const { TabPane } = Tabs;

// const NEW_SEARCH_RESULT_LIST_LOCALES = [];
const NEW_SEARCH_RESULT_LIST_LOCALES = ['us'];

export default function Search({ keyword, SEO }: any) {
    const [isMobile, setIsMobile] = useState(false);
    const router = useRouter();
    const { locale } = router;
    const [searchRes, setSearchRes]: any = useState({}); // 返回数据
    const [aiSearchRes, setAiSearchRes]: any = useState({}); // AI搜索返回数据
    const uuid = useRef<string>(''); // 不要直接读 sessionStorage
    const userId = loginUser()?.user?.userId;

    const [count, setCount] = useState({
        products: 0,
        press: 0,
        support: 0,
        total: 0,
        blog: 0,
        faq: 0,
        supports: 0,
        news: 0,
    }); // 各个tab的数量以及总数

    const [aiCount, setAiCount]: any = useState({
        products: 0,
        shopifyProducts: 0,
        supports: 0,
        faqs: 0,
    }); // AI搜索返回数据

    const [loading, setLoading] = useState(false); // loading
    const [value, setValue] = useState('');
    const [currentKey, setCurrentKey] = useState('products');
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useLang('shared');

    const KEY_MAP = {
        blog: 'EcoFlow Blog',
        faq: 'FAQ',
        products: 'Product',
    };

    // 计算res各项total
    const calculateTotal = (searchRes: any) => {
        const res: any = { total: 0 };
        Object.keys(searchRes).forEach((key) => {
            res[key] = searchRes[key].length;
            res['total'] += res[key];
        });
        res.blog = (res?.news || 0) + (res?.supports || 0);
        return res;
    };

    // 计算res各项total
    const calculateAITotal = (searchRes: any) => {
        const res: any = {
            products: 0,
            shopifyProducts: 0,
            supports: 0,
            faqs: 0,
            total: 0,
        };

        // 遍历返回的数据，更新对应字段的 total
        Object.keys(searchRes).forEach((key) => {
            if (searchRes[key] && typeof searchRes[key].total === 'number') {
                res[key] = searchRes[key].total;
                res['total'] += res[key];
            }
        });

        return res;
    };

    // 获取search接口res
    const handleSearch = async (keyWord: string) => {
        setLoading(true); // 打开loading
        try {
            const res: any = await getSearchRes({
                keyWord,
                searchScope: 'products,faq,supports',
            });
            setLoading(false); // 关闭loading
            if (res.code === '0') {
                setSearchRes(res.data || {});
                setCount({ ...count, ...calculateTotal(res.data) });
                return;
            }
        } catch (e) {
            setLoading(false); // 关闭loading
            console.log('e', e);
        }
    };
    // 根据返回数据计算默认选中的 tab
    const getDefaultCurrentKey = (data: any) => {
        const productsCount = data?.products?.total || 0;
        const supportsCount = data?.supports?.total || 0;
        const faqsCount = data?.faqs?.total || 0;

        if (productsCount > 0) {
            return 'products';
        }
        if (supportsCount > 0) {
            return 'blog';
        }
        if (faqsCount > 0) {
            return 'faq';
        }
        return 'products';
    };

    // 获取ai search接口res
    const handleAiSearch = async (keyWord: string, sessionId: string, page = 1, isInitial = false) => {
        setLoading(true); // 打开loading
        try {
            const params = {
                query: keyWord,
                page: page || 1,
                pageSize: 8,
                scene: 'page',
                searchScope: 'products,supports,faqs',
                sessionId: sessionId || uuid.current || '',
                userId,
            };
            const res: any = await getAISearchRes(params);
            setLoading(false); // 关闭loading
            if (res.code === '0') {
                setAiSearchRes(res.data || {});
                if (!!isEmpty(res.data)) {
                    setAiCount({
                        products: 0,
                        shopifyProducts: 0,
                        supports: 0,
                        faqs: 0,
                        total: 0,
                    });
                } else {
                    setAiCount(calculateAITotal(res.data));
                }

                // 只在初次请求时根据返回数据设置默认选中的 tab
                if (isInitial) {
                    setCurrentKey(getDefaultCurrentKey(res.data));
                }
                return;
            }
        } catch (e) {
            setLoading(false); // 关闭loading
            console.log('e', e);
        }
    };

    // 切换tab
    const handleChangeTabs = (key: string) => {
        if (NEW_SEARCH_RESULT_LIST_LOCALES?.includes(locale as string)) {
            // 切换 tab 需要重新请求 page = 1 的数据
            handleAiSearch(keyword || '', uuid.current, 1);
            setCurrentPage(1);
        } else {
            const navigationName = KEY_MAP[key as keyof typeof KEY_MAP];
            if (navigationName)
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'search_navigation',
                    navigation_name: navigationName,
                });
        }

        setCurrentKey(key);
    };

    // 点击搜索
    const onEnter = (e: any) => {
        const keyword = e.target.value;
        if (e.keyCode !== 13 || !keyword) return;
        gtm.push({
            event: 'uaEvent',
            event_name: 'search',
            search_term: keyword,
            button_name: '',
            type: 'Enter',
        });
        router.push('/search/[keyword]', `/${locale}/search/${keyword}`);

        if (!!NEW_SEARCH_RESULT_LIST_LOCALES.includes(locale as string)) {
            if (typeof window !== 'undefined') {
                const searchEntry: any = localStorage.getItem('search_entry');

                gtm.push({
                    event: 'uaEvent',
                    event_name: 'search',
                    search_term: keyword,
                    button_name: keyword,
                    type: 'Enter',
                    is_input: 'Yes',
                    source_method: searchEntry || '',
                });
            }
        }
    };

    // 输入框change
    const handleChange = (e: any) => {
        const value = e.target.value;
        setValue(value);
    };

    // 清空输入框
    const clearValue = () => {
        setValue('');
    };

    const handlePageChange = (page: number) => {
        console.log(page, 'page change');
        setCurrentPage(page);
        handleAiSearch(keyword || '', uuid.current, page, false);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const sessionId = sessionStorage.getItem('ef_search_ai_session_id') || '';
            uuid.current = sessionId;

            if (!!NEW_SEARCH_RESULT_LIST_LOCALES.includes(locale as string)) {
                handleAiSearch(keyword || '', sessionId, 1, true);
                setCurrentKey('products');
            } else {
                handleSearch(keyword || '');
            }
        }

        setValue(keyword);

        gtm.EmPush(['searchTerm', keyword]);
    }, [keyword]);

    useEffect(() => {
        setIsMobile(getIsPadWidth());
    }, []);

    const titleMap: any = {
        river: t('searchkeyword.seo_title_river'),
        delta: t('searchkeyword.seo_title_delta'),
    };

    const infoList = [
        {
            title: t('searchkeyword.site_search_info_title1'),
            icon: <EFAIDownloadIcon />,
            desc: t('searchkeyword.site_search_info_desc1'),
            learnMoreText: 'Learn More',
            link: `https://www.ecoflow.com/${locale}/support/download/index`,
        },
        {
            title: t('searchkeyword.site_search_info_title2'),
            icon: <EFCoinIcon />,
            desc: t('searchkeyword.site_search_info_desc2'),
            learnMoreText: 'Learn More',
            link: `https://www.ecoflow.com/${locale}/ecocredits`,
        },
        {
            title: t('searchkeyword.site_search_info_title3'),
            icon: <EFStoreIcon />,
            desc: t('searchkeyword.site_search_info_desc3'),
            learnMoreText: 'Learn More',
            link: `https://www.ecoflow.com/${locale}/where-to-buy`,
        },
    ];

    return (
        <PageLayout>
            <SEO title={titleMap[keyword] || titleMap.river} description={''} />
            <SEOlinksA
                locale={locale as string}
                list={seoconfig[keyword as keyof typeof seoconfig]?.include}
                pathname={seoconfig[keyword as keyof typeof seoconfig]?.url}
            />
            {!!NEW_SEARCH_RESULT_LIST_LOCALES.includes(locale as string) && (
                <div className={css.newSearchResultBox}>
                    <div className="searchBarContent">
                        <input value={value} onChange={handleChange} onKeyDown={onEnter} type="text" aria-label={t('searchkeyword.title')} />
                        {/* 搜索与关闭图标 */}
                        <div className="searchIcon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="11" cy="11" r="6" stroke="black" strokeWidth="1.6" />
                                <path
                                    d="M19.3841 20.5154C19.6965 20.8279 20.203 20.8279 20.5154 20.5154C20.8279 20.203 20.8279 19.6965 20.5154 19.3841L19.9497 19.9497L19.3841 20.5154ZM15 15L14.4343 15.5657L19.3841 20.5154L19.9497 19.9497L20.5154 19.3841L15.5657 14.4343L15 15Z"
                                    fill="black"
                                />
                            </svg>
                        </div>
                        {!!value && (
                            <div className="closeIcon" onClick={clearValue}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clipPath="url(#clip0_466_38365)">
                                        <rect width="20" height="20" rx="10" fill="#83868A" />
                                        <path
                                            d="M13.3334 6.66667L6.66675 13.3333"
                                            stroke="white"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M6.66675 6.66667L13.3334 13.3333"
                                            stroke="white"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_466_38365">
                                            <rect width="20" height="20" rx="10" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        )}
                    </div>
                    {/* 内容区域 */}
                    <div className="searchResContainer">
                        <Tabs className="aiSearchTabs" onChange={handleChangeTabs} activeKey={currentKey}>
                            <TabPane tab={`${t('searchkeyword.site_explore')}(${aiCount?.products || 0})`} key="products">
                                {!loading ? (
                                    <>
                                        {aiCount?.total > 0 ? (
                                            <AIProducts
                                                products={aiSearchRes?.products}
                                                shopifyProducts={aiSearchRes.shopifyProducts}
                                                isMobile={isMobile}
                                                onPageChange={handlePageChange}
                                                currentPage={currentPage}
                                            />
                                        ) : (
                                            <AIGenerate keyword={keyword} />
                                        )}
                                    </>
                                ) : (
                                    <Skeleton />
                                )}
                            </TabPane>
                            <TabPane tab={`${t('searchkeyword.site_articles')}(${aiCount?.supports || 0})`} key="blog">
                                {!loading ? (
                                    <AIPress
                                        press={aiSearchRes.supports}
                                        news={aiSearchRes.news}
                                        isMobile={isMobile}
                                        onPageChange={handlePageChange}
                                        currentPage={currentPage}
                                    />
                                ) : (
                                    <Skeleton />
                                )}
                            </TabPane>

                            <TabPane tab={`${t('searchkeyword.site_search_faq')}(${aiCount?.faqs || 0})`} key="faq">
                                {!loading ? (
                                    <AISupports faqs={aiSearchRes.faqs} isMobile={isMobile} onPageChange={handlePageChange} currentPage={currentPage} />
                                ) : (
                                    <Skeleton />
                                )}
                            </TabPane>
                        </Tabs>
                    </div>
                    <div className="aiSearchInfo">
                        <div className="aiSearchInfoHeader">
                            <div className="aiSearchInfoHeaderLine aiSearchInfoHeaderLineLeft" />
                            <div className="aiSearchInfoTitle">{t('searchkeyword.site_search_info')}</div>
                            <div className="aiSearchInfoHeaderLine aiSearchInfoHeaderLineRight" />
                        </div>
                        <div className="aiSearchInfoList">
                            {infoList?.map((item, index) => (
                                <div key={index} className="aiSearchInfoItem">
                                    <div className="aiSearchInfoItemIcon">{item.icon}</div>
                                    <div className="aiSearchInfoItemContent">
                                        <div className="aiSearchInfoItemTitle">{item.title}</div>
                                        <div className="aiSearchInfoItemDesc">{item.desc}</div>
                                        <EcoLink className="aiSearchInfoItemLearnMore" href={item?.link}>
                                            {item.learnMoreText}
                                        </EcoLink>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {!NEW_SEARCH_RESULT_LIST_LOCALES.includes(locale as string) && (
                <div className={`${css.search} page-search`}>
                    {/* 搜索区域 */}
                    <div className="searchBarContainer searchBarContainer-pc">
                        <div className="searchBackground">
                            <Image
                                className="backImg"
                                src="https://websiteoss.ecoflow.com/upload/image/1761128386926/Container.png"
                                layout="fill"
                                alt={t('searchkeyword.title')}
                            />
                        </div>
                        <div className="searchBarContent">
                            <input value={value} onChange={handleChange} onKeyDown={onEnter} type="text" aria-label={t('searchkeyword.title')} />
                            {/* 搜索与关闭图标 */}
                            <div className="searchIcon">
                                <EFHeaderSearch />
                            </div>
                            {!!value && (
                                <div className="closeIcon" onClick={clearValue}>
                                    <CloseOutlined />
                                </div>
                            )}
                        </div>
                    </div>
                    {/* 搜索区域-h5 */}
                    <div className="searchBarContainer searchBarContainer-h5">
                        <div className="ef-input-search-primal">
                            <input
                                value={value}
                                onChange={handleChange}
                                onKeyDown={onEnter}
                                type="text"
                                className="ef-input-search-primal-input"
                                placeholder={t('searchkeyword.search_web')}
                                aria-label={t('searchkeyword.search_web')}
                            />
                            {/* 搜索与关闭图标 */}
                            <div className="ef-input-search-primal-searchicon">
                                <EFHeaderSearchForSearch />
                            </div>
                            {!!value && (
                                <div className="ef-input-search-primal-closeicon" onClick={clearValue}>
                                    <CloseOutlined />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 内容区域 */}
                    <div className="searchResContainer">
                        <Tabs
                            onChange={handleChangeTabs}
                            activeKey={currentKey}
                            tabBarExtraContent={
                                <span className="leftTotalRes">
                                    {t('searchkeyword.site_search_result', {
                                        number: count.total || 0,
                                    })}
                                </span>
                            }
                        >
                            <TabPane
                                tab={`${isMobile ? t('searchkeyword.site_search_products') : t('searchkeyword.site_search_product')}${
                                    isMobile ? '' : `(${count.products || 0})`
                                }`}
                                key="products"
                            >
                                {!loading ? <Products products={searchRes?.products} isMobile={isMobile} /> : <Skeleton />}
                            </TabPane>
                            {!['eu', 'br', 'mx', 'ph'].includes(locale as string) && (
                                <TabPane
                                    tab={`${isMobile ? t('searchkeyword.site_search_press') : t('searchkeyword.site_search_blog')}${
                                        isMobile ? '' : `(${count.supports + count.news || 0})`
                                    }`}
                                    key="blog"
                                >
                                    {!loading ? <Press press={searchRes.supports} news={searchRes.news} isMobile={isMobile} /> : <Skeleton />}
                                </TabPane>
                            )}

                            <TabPane tab={`${t('searchkeyword.site_search_faq')}${isMobile ? '' : `(${count.faq || 0})`}`} key="faq">
                                {!loading ? <Supports faqs={searchRes.faq} isMobile={isMobile} /> : <Skeleton />}
                            </TabPane>
                        </Tabs>
                        {!!count[currentKey as keyof typeof count] && !isMobile && (
                            <p className="endRes font-family-regular">{t('searchkeyword.site_search_result_end')}</p>
                        )}
                        {!!count[currentKey as keyof typeof count] && isMobile && (
                            <p
                                className="endRes font-family-regular"
                                style={{
                                    marginTop: currentKey === 'products' ? '.59rem' : currentKey === 'faq' ? '.805rem' : '.48rem',
                                }}
                            >
                                {t('searchkeyword.site_search_result_end_tab', {
                                    number:
                                        currentKey === 'products'
                                            ? count.products || 0
                                            : currentKey === 'faq'
                                            ? count.faq || 0
                                            : currentKey === 'blog'
                                            ? count.supports + count.news || 0
                                            : 0,
                                })}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </PageLayout>
    );
}

export const getServerSideProps = async ({ params }: any) => {
    const keyword = params.keyword;
    return {
        props: { keyword },
    };
};
